import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface MarketsProps {
  t: any;
  isRTL: boolean;
}

const MarketsSection = styled.section`
  padding: 8rem 2rem;
  background: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="circuit" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="%23dc2626" opacity="0.1"/><path d="M 0 10 L 20 10 M 10 0 L 10 20" stroke="%23dc2626" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23circuit)"/></svg>');
    opacity: 0.3;
  }
`;

const Container = styled.div<{ $isRTL: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 6rem;
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 3.5rem;
  color: #1f2937;
  margin-bottom: 1.5rem;
  position: relative;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 6px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 3px;
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const SectionSubtitle = styled(motion.h3)<{ $isRTL: boolean }>`
  font-size: 1.6rem;
  color: #1f2937;
  font-weight: 400;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  margin-top: 2.5rem;
  
`;

const MarketsGrid = styled.div<{ $isRTL: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  margin-bottom: 6rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const MarketCard = styled(motion.div)<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(220, 38, 38, 0.1);
  padding: 3.5rem 2.5rem;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(220, 38, 38, 0.1) 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-20px) scale(1.02);
    border-color: rgba(220, 38, 38, 0.3);
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.3);
    
    &::before {
      transform: scaleX(1);
    }
    
    &::after {
      opacity: 1;
    }
  }
  
  &:hover .market-icon {
    transform: scale(1.15) rotate(10deg);
  }
`;

const MarketIcon = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2.5rem;
  box-shadow: 0 15px 35px rgba(220, 38, 38, 0.4);
  transition: all 0.4s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    background: linear-gradient(135deg, #dc2626, #b91c1c, #dc2626);
    border-radius: 30px;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  img {
    width: 60px;
    height: 60px;
    filter: brightness(0) invert(1);
    transition: all 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const MarketTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  line-height: 1.3;
  
`;

const MarketDescription = styled.p<{ $isRTL: boolean }>`
  color: #1f2937;
  line-height: 1.7;
  font-size: 1.1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  margin-bottom: 2rem;
  
`;

const MarketFeatures = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  justify-content: center;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const FeatureTag = styled(motion.span)`
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.2) 0%, rgba(185, 28, 28, 0.2) 100%);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: #fca5a5;
  padding: 0.6rem 1.2rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
    color: #1f2937;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.3) 0%, rgba(185, 28, 28, 0.3) 100%);
    border-color: rgba(220, 38, 38, 0.5);
    color: #1f2937;
    transform: translateY(-2px);
  }
`;

const AdvantagesSection = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(220, 38, 38, 0.1);
  padding: 5rem 3rem;
  border-radius: 30px;
  color: #1f2937;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="hex" width="20" height="20" patternUnits="userSpaceOnUse"><polygon points="10,2 18,6 18,14 10,18 2,14 2,6" fill="none" stroke="%23dc2626" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23hex)"/></svg>');
    animation: rotate 30s linear infinite;
  }
  
  @keyframes rotate {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const AdvantagesContent = styled.div`
  position: relative;
  z-index: 2;
`;

const AdvantagesTitle = styled(motion.h3)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
`;

const AdvantagesSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #1f2937;
  margin-bottom: 3rem;
  
`;

const AdvantagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;
`;

const AdvantageItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  text-align: left;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(220, 38, 38, 0.1);
  padding: 2rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(220, 38, 38, 0.3);
    transform: translateY(-5px);
  }
  
  .icon {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 15px;
    box-shadow: 0 8px 20px rgba(220, 38, 38, 0.3);
    
    img {
      width: 30px;
      height: 30px;
      filter: brightness(0) invert(1);
    }
  }
  
  .text {
    font-size: 1.1rem;
    line-height: 1.6;
    font-weight: 500;
    color: #1f2937;
    
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
`;

const FloatingShape = styled(motion.div)`
  position: absolute;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 50%;
  
  &:nth-child(1) {
    width: 300px;
    height: 300px;
    top: 10%;
    left: 5%;
  }
  
  &:nth-child(2) {
    width: 200px;
    height: 200px;
    top: 60%;
    right: 10%;
  }
  
  &:nth-child(3) {
    width: 150px;
    height: 150px;
    bottom: 20%;
    left: 15%;
  }
`;

const Markets: React.FC<MarketsProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const markets = [
    {
      title: t.market2,
      description: t.market2Desc,
      icon: "/images/markets/Industrie.svg",
      features: t.marketFeatures.industry
    },
    {
      title: t.market3,
      description: t.market3Desc,
      icon: "/images/markets/Infrastructure.svg",
      features: t.marketFeatures.infrastructure
    },
    {
      title: t.market1,
      description: t.market1Desc,
      icon: "/images/markets/Bâtiment & Tertiaire.svg",
      features: t.marketFeatures.building
    }
  ];

  const advantages = [
    { 
      icon: "/images/avantages/Économies d'échelle à travers des prix compétitifs.svg", 
      text: t.advantages[0]
    },
    { 
      icon: "/images/avantages/Sécurité en termes de qualité d'approvisionnement.svg", 
      text: t.advantages[1]
    },
    { 
      icon: "/images/avantages/Savoir-faire particulier TECHYID.svg", 
      text: t.advantages[2]
    },
    { 
      icon: "/images/avantages/Partenariats avec des grands fabricants mondiaux.svg", 
      text: t.advantages[3]
    },
    { 
      icon: "/images/avantages/Mise en disponibilité des produits.svg", 
      text: t.advantages[4]
    },
    { 
      icon: "/images/avantages/Assistance technique gratuite.svg", 
      text: t.advantages[5]
    },
    { 
      icon: "/images/avantages/Assistance pour la soumission à des marchés.svg", 
      text: t.advantages[6]
    },
    { 
      icon: "/images/avantages/Échanges d'expériences.svg", 
      text: t.advantages[7]
    }
  ];

  return (
    <MarketsSection id="markets" ref={ref}>
      <FloatingElements>
        <FloatingShape
          animate={{
            y: [0, -30, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <FloatingShape
          animate={{
            y: [0, 25, 0],
            rotate: [360, 180, 0],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <FloatingShape
          animate={{
            y: [0, -20, 0],
            rotate: [0, -180, -360],
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </FloatingElements>
      
      <Container $isRTL={isRTL}>
        <SectionHeader>
          <SectionTitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {t.marketsTitle}
          </SectionTitle>
          <SectionSubtitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {t.marketsSubtitle}
          </SectionSubtitle>
        </SectionHeader>

        <MarketsGrid $isRTL={isRTL}>
          {markets.map((market, index) => (
            <MarketCard
              key={index}
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 60 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ 
                duration: 0.8, 
                delay: 0.5 + index * 0.2,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
            >
              <MarketIcon className="market-icon">
                <img src={market.icon} alt={market.title} />
              </MarketIcon>
              <MarketTitle $isRTL={isRTL}>{market.title}</MarketTitle>
              <MarketDescription $isRTL={isRTL}>{market.description}</MarketDescription>
              <MarketFeatures $isRTL={isRTL}>
                {market.features.map((feature: string, featureIndex: number) => (
                  <FeatureTag
                    key={featureIndex}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ 
                      duration: 0.5, 
                      delay: 0.8 + index * 0.2 + featureIndex * 0.1 
                    }}
                    whileHover={{ scale: 1.1 }}
                  >
                    {feature}
                  </FeatureTag>
                ))}
              </MarketFeatures>
            </MarketCard>
          ))}
        </MarketsGrid>

        <AdvantagesSection>
          <AdvantagesContent>
            <AdvantagesTitle
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              {t.advantagesTitle}
            </AdvantagesTitle>
            <AdvantagesSubtitle
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.4 }}
            >
{t.advantagesSubtitle}
            </AdvantagesSubtitle>
            <AdvantagesGrid>
              {advantages.map((advantage: any, index: number) => (
                <AdvantageItem
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ 
                    duration: 0.6, 
                    delay: 1.6 + index * 0.1,
                    type: "spring",
                    stiffness: 100
                  }}
                  whileHover={{ 
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                >
                  <div className="icon">
                    <img src={advantage.icon} alt={advantage.text} />
                  </div>
                  <div className="text">{advantage.text}</div>
                </AdvantageItem>
              ))}
            </AdvantagesGrid>
          </AdvantagesContent>
        </AdvantagesSection>
      </Container>
    </MarketsSection>
  );
};

export default Markets;
